import { ApiError } from './api-error';

const BASE = '/api/v1';

function parseFilename(header) {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // Malformed percent-encoding: fall through to the plain filename below.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}

/**
 * Binary counterpart of `apiRequest` (./client.js), for the two shapes that
 * module can't express: sending a raw file body, and receiving a downloadable
 * file back instead of JSON. Shared by every gym admin flow that uploads or
 * downloads a spreadsheet — HAMMER 1D's import wizard (`uploadBinary` for
 * `POST /members/import/uploads`, `fetchBlob` for the `.xlsx` the commit and
 * the "download result again" endpoints answer) and the member/payment
 * export downloads (`fetchBlob` again, as a plain GET) — so the request/
 * response plumbing (credentials, network-error normalization, AbortError
 * passthrough) lives in exactly one place instead of being re-implemented
 * per page.
 */

/**
 * POSTs a raw binary body (a `File`/`Blob`, never JSON.stringify'd) and
 * parses a normal `{ ... }` JSON response back, the same way `apiRequest`
 * does for everything else. `contentType` should be the file's own mime type
 * (never `application/json`) — callers should pass a sane fallback when
 * `file.type` is empty, so the server's content-type filter always has
 * something to match against.
 */
export async function uploadBinary(path, body, { contentType, fetchImpl = fetch, signal } = {}) {
  let response;
  try {
    response = await fetchImpl(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: contentType ? { 'Content-Type': contentType } : undefined,
      body,
    });
  } catch (cause) {
    if (cause && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'network_error', 'No se pudo conectar con el servidor. Revisá tu conexión e intentá de nuevo.');
  }
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw ApiError.fromResponse(response.status, payload);
  return payload;
}

/**
 * Sends a request that expects a downloadable FILE back, not `{ ... }` JSON
 * — the response counterpart `apiRequest` can't express. `body` (when given)
 * is still sent as JSON, exactly like `apiRequest`; only the response is
 * binary. A failure response is still the normal `{ error: { code, message
 * } }` envelope: it's read as text first and only decoded as JSON if that
 * succeeds, so a malformed or empty error body never throws while trying to
 * report the original error.
 *
 * Any extra field the server put alongside `code`/`message` — either nested
 * inside `error` (e.g. the import commit's `resultAvailable` on a
 * `409 import_already_committed`) or at the payload's top level — is copied
 * onto the thrown `ApiError` instance instead of being dropped. `ApiError`
 * itself only models the universal `{ code, message }` shape, so
 * endpoint-specific extras travel as extra properties callers can read off
 * the same error they already have to catch.
 *
 * `summaryHeader`, when given, is read off a successful response and parsed
 * as JSON defensively (a missing or malformed header never fails the
 * download — it's presentational, not the source of truth, e.g. the import
 * commit's `X-Import-Summary` counts). The filename comes from
 * `Content-Disposition` (the RFC 5987 `filename*=UTF-8''...` form preferred,
 * a plain `filename="..."` as fallback); `null` when neither is present, so
 * callers can substitute their own default.
 */
export async function fetchBlob(path, {
  method = 'GET', body, summaryHeader, fetchImpl = fetch, signal,
} = {}) {
  let response;
  try {
    response = await fetchImpl(`${BASE}${path}`, {
      method,
      credentials: 'include',
      signal,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    if (cause && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'network_error', 'No se pudo conectar con el servidor. Revisá tu conexión e intentá de nuevo.');
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let payload = null;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      payload = null;
    }
    const apiError = ApiError.fromResponse(response.status, payload);
    const envelope = payload && typeof payload === 'object' ? payload.error : null;
    if (envelope && typeof envelope === 'object') {
      for (const [key, value] of Object.entries(envelope)) {
        if (key !== 'code' && key !== 'message') apiError[key] = value;
      }
    }
    if (payload && typeof payload === 'object') {
      for (const [key, value] of Object.entries(payload)) {
        if (key !== 'error') apiError[key] = value;
      }
    }
    throw apiError;
  }
  const blob = await response.blob();
  const filename = parseFilename(response.headers.get('content-disposition'));
  let summary = null;
  if (summaryHeader) {
    const raw = response.headers.get(summaryHeader);
    if (raw) {
      try {
        summary = JSON.parse(raw);
      } catch {
        summary = null;
      }
    }
  }
  return { blob, filename, summary };
}

/**
 * Triggers a browser "Save as" for a blob fetched via `fetchBlob`/
 * `uploadBinary`, via a throwaway `<a download>` + object URL — no server
 * round trip, no new tab/window. Safe to call more than once with the same
 * blob (e.g. a "Descargar de nuevo" button): each call creates and revokes
 * its own object URL. The URL is revoked after the synthetic anchor click;
 * environments that do not implement revocation must not break downloads.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'descarga';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // Object URL cleanup is best-effort and must never break the download.
    }
  }
}
